import "./App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Toaster } from "sonner";

import AppShell from "./components/layout/AppShell";
import OfflineBanner from "./components/common/OfflineBanner";
import { AuthProvider } from "./lib/auth";
import AdminEntretienScript from "./pages/AdminEntretienScript";
import AdminAssociations from "./pages/AdminAssociations";
import CallCenter from "./pages/CallCenter";
import AnalyticsCalls from "./pages/AnalyticsCalls";
import CandidateFocus from "./pages/CandidateFocus";
import CandidateRecap from "./pages/CandidateRecap";
import Candidates from "./pages/Candidates";
import CandidateProfile from "./pages/CandidateProfile";
import Confidentialite from "./pages/Confidentialite";
import Dashboard from "./pages/Dashboard";
import Embauche from "./pages/Embauche";
import EmbaucheAudit from "./pages/EmbaucheAudit";
import EmailLog from "./pages/EmailLog";
import Corbeille from "./pages/Corbeille";
import ImmersionCalendar from "./pages/ImmersionCalendar";
import IntegrationsLayan from "./pages/IntegrationsLayan";
import IntegrationsSite from "./pages/IntegrationsSite";
import ImmersionConfirm from "./pages/ImmersionConfirm";
import Login from "./pages/Login";
import MentionsLegales from "./pages/MentionsLegales";
import MessageTemplatesAdmin from "./pages/MessageTemplatesAdmin";
import Notifications from "./pages/Notifications";
import Offres from "./pages/Offres";
import OnboardingForm from "./pages/OnboardingForm";
import Permanents from "./pages/Permanents";
import Equipment from "./pages/Equipment";
import EquipmentNoteSign from "./pages/EquipmentNoteSign";
import MyEquipmentNotes from "./pages/MyEquipmentNotes";
import Procedures from "./pages/Procedures";
import PermanentDetail from "./pages/PermanentDetail";
import ContractSign from "./pages/ContractSign";
import PermanentContractSign from "./pages/PermanentContractSign";
import PermanentAvenantSign from "./pages/PermanentAvenantSign";
import Profile from "./pages/Profile";
import Scorecard from "./pages/Scorecard";
import LoginHistory from "./pages/LoginHistory";
import TeamAnalytics from "./pages/TeamAnalytics";
import TenantSettingsAdmin from "./pages/TenantSettingsAdmin";
import ContractOptionsAdmin from "./pages/ContractOptionsAdmin";
import ContractTemplateAdmin from "./pages/ContractTemplateAdmin";
import TestCandidate from "./pages/TestCandidate";
import Users from "./pages/Users";
import Vivier from "./pages/Vivier";
import Refuses from "./pages/Refuses";
import Ecartes from "./pages/Ecartes";
import GrowthCandidates from "./pages/GrowthCandidates";
import GrowthPrequal from "./pages/GrowthPrequal";
import GrowthShortlist from "./pages/GrowthShortlist";
import GrowthTraites from "./pages/GrowthTraites";
import MesImmersions from "./pages/MesImmersions";
import CodeRdAdmin from "./pages/CodeRdAdmin";
import RequireAdmin from "./components/common/RequireAdmin";

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster
                    richColors
                    position="top-right"
                    toastOptions={{
                        style: { fontFamily: "Montserrat, sans-serif", fontWeight: 600 },
                    }}
                />
                <Routes>
                    <Route path="/login" element={<Login />} />
                    {/* Legal pages — public, no auth */}
                    <Route path="/mentions-legales" element={<MentionsLegales />} />
                    <Route path="/confidentialite" element={<Confidentialite />} />
                    {/* Public candidate-facing magic-link page (no auth, no AppShell) */}
                    <Route path="/i/:token" element={<ImmersionConfirm />} />
                    {/* Public candidate-facing onboarding form (no auth, no AppShell) */}
                    <Route path="/onboarding/:token" element={<OnboardingForm />} />
                    {/* Public candidate-facing contract signing page (no auth, no AppShell) */}
                    <Route path="/contract/:token" element={<ContractSign />} />
                    <Route path="/permanent-sign/:token" element={<PermanentContractSign />} />
                    <Route path="/permanent-avenant-sign/:token" element={<PermanentAvenantSign />} />
                    <Route path="/signer-bon/:token" element={<EquipmentNoteSign />} />
                    {/* Focus mode — NO sidebar, NO topbar (auth still enforced via useAuth in the page) */}
                    <Route path="/candidats/:id/focus" element={<CandidateFocus />} />
                    <Route element={<AppShell />}>
                        <Route path="/" element={<Navigate to="/dashboard" replace />} />
                        <Route path="/dashboard" element={<Dashboard />} />
                        <Route path="/candidats" element={<Candidates />} />
                        <Route path="/candidats/:id" element={<CandidateProfile />} />
                        <Route path="/candidats/:id/recap" element={<CandidateRecap />} />
                        <Route path="/candidats/:id/scorecard/:type" element={<Scorecard />} />
                        <Route path="/calendrier-immersions" element={<ImmersionCalendar />} />
                        <Route path="/vivier" element={<Vivier />} />
                        <Route path="/mes-immersions" element={<MesImmersions />} />
                        <Route path="/ecartes" element={<Ecartes />} />
                        <Route path="/refuses" element={<Refuses />} />
                        <Route path="/growth" element={<GrowthCandidates />} />
                        <Route path="/growth/shortlist" element={<GrowthShortlist />} />
                        <Route path="/growth/traites" element={<GrowthTraites />} />
                        <Route path="/growth/:id/prequal" element={<GrowthPrequal />} />
                        <Route path="/offres" element={<Offres />} />
                        <Route path="/notifications" element={<Notifications />} />
                        <Route path="/embauche" element={<Embauche />} />
                        <Route path="/embauche/audit" element={<EmbaucheAudit />} />
                        <Route path="/permanents" element={<Permanents />} />
                        <Route path="/permanents/:pid" element={<PermanentDetail />} />
                        <Route path="/materiel" element={<Equipment />} />
                        <Route path="/mes-bons" element={<MyEquipmentNotes />} />
                        <Route path="/procedures" element={<Procedures />} />
                        <Route path="/appels" element={<CallCenter />} />
                        <Route path="/analytics-appels" element={<AnalyticsCalls />} />
                        <Route path="/pepites" element={<Candidates kind="pepites" />} />
                        <Route path="/alertes" element={<Candidates kind="alertes" />} />
                        <Route path="/utilisateurs" element={<Users />} />
                        <Route path="/admin/modeles-messages" element={<MessageTemplatesAdmin />} />
                        <Route path="/admin/parametres-entreprise" element={<TenantSettingsAdmin />} />
                        <Route path="/admin/emails" element={<EmailLog />} />
                        <Route path="/admin/corbeille" element={<Corbeille />} />
                        <Route path="/admin/roles-regions" element={<ContractOptionsAdmin />} />
                        <Route path="/admin/modele-contrat" element={<ContractTemplateAdmin />} />
                        <Route path="/admin/associations" element={<AdminAssociations />} />
                        <Route path="/admin/candidat-test" element={<TestCandidate />} />
                        <Route path="/admin/codes-rd" element={
                            <RequireAdmin>
                                <CodeRdAdmin />
                            </RequireAdmin>
                        } />
                        <Route path="/mon-profil" element={<Profile />} />
                        <Route path="/integrations/layan" element={<IntegrationsLayan />} />
                        <Route path="/integrations/site" element={<IntegrationsSite />} />
                        <Route path="/admin/trame" element={<AdminEntretienScript />} />
                        <Route path="/admin/analyses" element={<TeamAnalytics />} />
                        <Route path="/admin/connexions" element={<LoginHistory />} />
                    </Route>
                    <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
                <OfflineBanner />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
